import React from "react";
import SourceSansPro from "@fontsource/source-sans-pro/files/source-sans-pro-latin-400-normal.woff";
import SourceSansProBold from "@fontsource/source-sans-pro/files/source-sans-pro-latin-600-normal.woff";
import SourceSansProItalic from "@fontsource/source-sans-pro/files/source-sans-pro-latin-400-italic.woff";
import {
  Document,
  Font,
  Image,
  Page,
  Path,
  Svg,
  Text,
  View,
} from "@react-pdf/renderer";
import { colors } from "@apollo/space-kit/colors";
import type { PageSize } from "@react-pdf/types/page";

Font.register({
  family: "Source Sans Pro",
  fonts: [
    {
      src: SourceSansPro,
    },
    {
      src: SourceSansProItalic,
      fontStyle: "italic",
    },
    {
      src: SourceSansProBold,
      fontWeight: 600,
    },
  ],
});

export const Certificate: React.FC<{
  title: string;
  owner?: {
    fullName: string;
  };
  issued: string;
  expiry: string;
  description: string;
  image: string;
  size?: PageSize;
}> = ({ title, owner, issued, expiry, description, image, size }) => {
  const padding = 56;
  const badgeSize = 148;
  return (
    <Document>
      <Page
        // TODO: Don't do undefined here
        size={size}
        orientation="landscape"
        style={{ fontFamily: "Source Sans Pro" }}
      >
        <View
          style={{
            display: "flex",
            height: "100%",
            padding,
            // account for badge and spacing around it
            paddingRight: badgeSize + padding + 48,
            justifyContent: "space-between",
          }}
        >
          <View
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Svg
              viewBox="0 0 136 41"
              style={{
                width: 108.8,
                height: 32,
              }}
            >
              <Path
                fill="black"
                d="M22.0692 11.3431L17.9769 11.3431L12.0684 26.7052H15.77L16.7355 24.103H22.3184L21.3077 21.2236H17.632L20.0232 14.6136L24.2766 26.7052L27.9778 26.7052L22.0692 11.3431Z"
              />
              <Path
                fill="black"
                d="M84.2363 26.7052V11.3431H87.524V23.8263H94.0071V26.7052H84.2363Z"
              />
              <Path
                fill="black"
                d="M103.186 26.7052V11.3431H106.473V23.8263H112.957V26.7052H103.186Z"
              />
              <Path
                fill="black"
                d="M58.0977 19.0361C58.0977 14.6478 61.6488 11.0902 66.0292 11.0902C70.4099 11.0902 73.9608 14.6478 73.9608 19.0361C73.9608 23.4245 70.4097 26.9822 66.0292 26.9822C61.6488 26.9822 58.0977 23.4245 58.0977 19.0361ZM70.9599 19.037C70.9599 16.3131 68.7479 14.0974 66.029 14.0974C63.3101 14.0974 61.0984 16.3129 61.0984 19.037C61.0984 21.7606 63.3101 23.9767 66.029 23.9767C68.7479 23.9767 70.9599 21.7606 70.9599 19.037Z"
              />
              <Path
                fill="black"
                d="M120.137 19.0361C120.137 14.6478 123.688 11.0902 128.068 11.0902C132.449 11.0902 136 14.6478 136 19.0361C136 23.4245 132.449 26.9822 128.068 26.9822C123.688 26.9822 120.137 23.4245 120.137 19.0361ZM132.999 19.037C132.999 16.3131 130.787 14.0974 128.068 14.0974C125.349 14.0974 123.137 16.3129 123.137 19.037C123.137 21.7606 125.349 23.9767 128.068 23.9767C130.787 23.9767 132.999 21.7606 132.999 19.037Z"
              />
              <Path
                fill="black"
                d="M40.8117 11.3429H44.6971C47.4225 11.3429 49.6319 13.6502 49.6319 16.3807C49.6319 19.1108 47.4225 21.4185 44.6971 21.4185H40.8117V26.7052H37.5469V11.3429H40.2543H40.8117ZM40.8101 18.4106H44.6955C45.7621 18.4106 46.6293 17.4477 46.6293 16.3794C46.6293 15.3111 45.7621 14.3481 44.6955 14.3481H40.8101V18.4106Z"
              />
              <Path
                fill="black"
                d="M34.7261 31.3667C34.442 31.3667 34.1912 31.5077 34.038 31.7239C34.038 31.7239 33.3016 32.5655 32.9045 32.9638C31.2247 34.6466 29.2689 35.9675 27.0915 36.8903C24.8381 37.8453 22.4437 38.3294 19.9743 38.3294C17.5046 38.3294 15.1102 37.8453 12.8571 36.8903C10.6796 35.9675 8.72374 34.6466 7.04414 32.9638C5.3642 31.2807 4.04549 29.3215 3.12456 27.1402C2.17164 24.8827 1.68819 22.4837 1.68819 20.01C1.68819 17.5363 2.17164 15.1373 3.12456 12.8798C4.04549 10.6985 5.3642 8.73934 7.04414 7.05654C8.72391 5.37357 10.6797 4.05265 12.8571 3.12989C15.1102 2.17491 17.5046 1.69076 19.9743 1.69076C22.4437 1.69076 24.8382 2.17491 27.0915 3.12989C28.6775 3.8019 30.1452 4.68638 31.475 5.76716C31.404 5.97677 31.3642 6.20156 31.3642 6.43546C31.3642 7.57694 32.288 8.50241 33.4273 8.50241C34.5669 8.50241 35.4907 7.57694 35.4907 6.43546C35.4907 5.2938 34.5669 4.36851 33.4273 4.36851C33.1445 4.36851 32.8747 4.42567 32.6291 4.52921C29.1839 1.69919 24.7773 3.05176e-05 19.9743 3.05176e-05C8.94274 3.05176e-05 0 8.95873 0 20.0102C0 31.0616 8.94274 40.0205 19.9743 40.0205C26.145 40.0205 31.6598 37.2151 35.3234 32.81C35.4759 32.6571 35.5707 32.4458 35.5707 32.2126C35.5705 31.7449 35.1928 31.3667 34.7261 31.3667Z"
              />
            </Svg>
            <View
              style={{
                backgroundColor: colors.indigo.lightest,
                textTransform: "uppercase",
                color: colors.indigo.dark,
                fontWeight: 600,
                paddingHorizontal: 8,
                paddingVertical: 1,
                borderRadius: 6,
                fontSize: 18,
                marginLeft: 12,
                marginTop: -4,
                letterSpacing: 1.8,
              }}
            >
              <Text>Certified</Text>
            </View>
          </View>
          <View>
            {owner && (
              <View
                style={{
                  fontSize: 48,
                  lineHeight: 1,
                  fontWeight: 600,
                  marginBottom: 8,
                }}
              >
                <Text>{owner.fullName}</Text>
              </View>
            )}
            <View
              style={{
                fontSize: 36,
                lineHeight: 1.2,
                fontWeight: 600,
                marginBottom: 16,
              }}
            >
              <Text>{title}</Text>
            </View>
            <View
              style={{
                lineHeight: 1.5,
                fontSize: 18,
              }}
            >
              <Text hyphenationCallback={(word) => [word]}>{description}</Text>
            </View>
          </View>
          <View style={{ fontSize: 18 }}>
            <View>
              <Text>{issued}</Text>
            </View>
            <View style={{ fontStyle: "italic" }}>
              <Text>{expiry}</Text>
            </View>
          </View>
        </View>
        <Image
          src={image}
          style={{
            width: badgeSize,
            height: badgeSize,
            position: "absolute",
            bottom: padding,
            right: padding,
          }}
        />
      </Page>
    </Document>
  );
};
